import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { floorplannerStore } from "../../store/floorplannerStore";
import {
  Currency,
  getCurrenciesWithPreferredFirst,
  getCurrencyDisplaySymbol,
} from "../../utils/currency";

type CurrencySelectorProps = {
  className?: string;
  /** Compact chip (matches language / unit toggles). */
  compact?: boolean;
  /** Smaller chip for tight panels (e.g. product-list price summary). */
  dense?: boolean;
  /**
   * Which edge of the trigger the menu aligns to.
   * Use `right` in the product-list panel so a wide menu does not overhang left.
   */
  menuAlign?: "left" | "right";
};

type MenuPosition = { top: number; left: number; width: number };

/**
 * Per-floorplan display currency picker. Preferred codes (SEK, EUR, …) first.
 * Menu is portaled + fixed so it cannot expand scroll parents / shift the panel.
 */
export const CurrencySelector: React.FC<CurrencySelectorProps> = observer(
  ({ className = "", compact = true, dense = false, menuAlign = "left" }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [saving, setSaving] = useState(false);
    const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const currentCurrency = floorplannerStore.currency;
    const disabled =
      floorplannerStore.lockedByOther ||
      floorplannerStore.collabAccessLost ||
      saving;

    const sortedCurrencies = useMemo(
      () => getCurrenciesWithPreferredFirst(),
      [],
    );

    const filteredCurrencies = useMemo(() => {
      if (!search.trim()) return sortedCurrencies;
      const q = search.trim().toLowerCase();
      return sortedCurrencies.filter((currency) => {
        const symbol = getCurrencyDisplaySymbol(currency).toLowerCase();
        return currency.toLowerCase().includes(q) || symbol.includes(q);
      });
    }, [sortedCurrencies, search]);

    const updateMenuPosition = () => {
      const trigger = containerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const width = 200;
      const gap = 4;
      const left =
        menuAlign === "right"
          ? Math.max(8, rect.right - width)
          : Math.min(
              Math.max(8, rect.left),
              window.innerWidth - width - 8,
            );
      // Prefer opening above the trigger; fall back below if near the top.
      const menuHeight = menuRef.current?.offsetHeight ?? 240;
      const openAbove = rect.top >= menuHeight + gap + 8;
      const top = openAbove
        ? rect.top - gap - menuHeight
        : rect.bottom + gap;
      setMenuPosition((prev) => {
        if (
          prev &&
          prev.top === top &&
          prev.left === left &&
          prev.width === width
        ) {
          return prev;
        }
        return { top, left, width };
      });
    };

    useLayoutEffect(() => {
      if (!isOpen) {
        setMenuPosition(null);
        return;
      }
      updateMenuPosition();
    }, [isOpen, menuAlign]);

    useLayoutEffect(() => {
      if (!isOpen || !menuPosition) return;
      // Re-measure once the menu has real height, then focus without scrolling
      // parents (avoids the product-list panel jumping left).
      updateMenuPosition();
      searchInputRef.current?.focus({ preventScroll: true });
    }, [isOpen, Boolean(menuPosition), filteredCurrencies.length, search, menuAlign]);

    useEffect(() => {
      if (!isOpen) return;
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        if (
          containerRef.current?.contains(target) ||
          menuRef.current?.contains(target)
        ) {
          return;
        }
        setIsOpen(false);
        setSearch("");
      };
      const handleReposition = () => updateMenuPosition();
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("resize", handleReposition);
      window.addEventListener("scroll", handleReposition, true);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener("resize", handleReposition);
        window.removeEventListener("scroll", handleReposition, true);
      };
    }, [isOpen, menuAlign]);

    const handleSelect = async (currency: Currency) => {
      if (currency === currentCurrency || disabled) return;
      setSaving(true);
      try {
        await floorplannerStore.setCurrency(currency);
        setIsOpen(false);
        setSearch("");
      } catch {
        // Reverted in store; keep picker open so the user can retry.
      } finally {
        setSaving(false);
      }
    };

    const currentSymbol = getCurrencyDisplaySymbol(currentCurrency);

    const menu =
      isOpen && menuPosition
        ? createPortal(
            <div
              ref={menuRef}
              style={{
                position: "fixed",
                top: menuPosition.top,
                left: menuPosition.left,
                width: menuPosition.width,
              }}
              className="z-[10000] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
            >
              <div className="border-b border-gray-200 p-2">
                <input
                  ref={searchInputRef}
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={t("currency.search", {
                    defaultValue: "Search currency",
                  })}
                  className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs focus:border-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-800"
                  onClick={(event) => event.stopPropagation()}
                />
              </div>
              <ul
                role="listbox"
                aria-label={t("currency.label", { defaultValue: "Currency" })}
                className="max-h-56 overflow-y-auto"
              >
                {filteredCurrencies.map((currency) => {
                  const symbol = getCurrencyDisplaySymbol(currency);
                  const label =
                    symbol === currency ? currency : `${symbol} ${currency}`;
                  return (
                    <li key={currency}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={currentCurrency === currency}
                        onClick={() => void handleSelect(currency)}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                          currentCurrency === currency
                            ? "bg-gray-50 font-medium"
                            : "text-gray-800"
                        }`}
                      >
                        {label}
                      </button>
                    </li>
                  );
                })}
                {filteredCurrencies.length === 0 && (
                  <li className="px-3 py-2 text-xs text-gray-500">
                    {t("currency.noResults", {
                      defaultValue: "No matching currencies",
                    })}
                  </li>
                )}
              </ul>
            </div>,
            document.body,
          )
        : null;

    return (
      <div ref={containerRef} className={`relative ${className}`.trim()}>
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-label={t("currency.label", { defaultValue: "Currency" })}
          title={t("currency.current", {
            defaultValue: "Currency: {{currency}}",
            currency: currentCurrency,
          })}
          disabled={disabled}
          onClick={() => {
            if (disabled) return;
            setIsOpen((open) => !open);
          }}
          className={
            compact
              ? dense
                ? "relative flex h-7 min-w-[2rem] items-center justify-center rounded-md border border-gray-200 bg-white px-1.5 text-[10px] font-semibold uppercase text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                : "relative flex h-9 min-w-[2.25rem] items-center justify-center rounded-lg border border-gray-200 bg-white px-1.5 text-[10px] font-semibold uppercase text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              : "rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 hover:border-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
          }
        >
          {currentSymbol}
        </button>
        {menu}
      </div>
    );
  },
);

export default CurrencySelector;
